import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AppShell from "../../components/layout/AppShell";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import { getVaultBalance, getVaultLedger } from "../../services/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// USDC: 7 decimal places
const fmtUsdc = (n) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 7, maximumFractionDigits: 7 });

// Fiat: floor to 2 decimal places (half-round-down)
const fmtFiat = (n) => {
  const floored = Math.floor(Number(n ?? 0) * 100) / 100;
  return floored.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtByCurrency = (n, currency) =>
  (currency || "").toUpperCase() === "USDC" ? fmtUsdc(n) : fmtFiat(n);

const statusLabel = (s) => {
  if (!s) return "—";
  const n = s.toLowerCase();
  if (n === "completed" || n === "success" || n === "credit") return "Completed";
  if (n === "debit" || n === "withdrawn") return "Withdrawn";
  if (n === "pending" || n === "processing" || n === "created") return "Processing";
  if (n === "failed" || n === "rejected") return "Failed";
  return s;
};

const statusColor = (s) => {
  const l = statusLabel(s);
  if (l === "Completed") return "text-green-600";
  if (l === "Withdrawn") return "text-purple-500";
  if (l === "Failed") return "text-red-500";
  return "text-orange-500";
};

const amountDisplay = (entry) => {
  const currency = entry.currency || "USDC";
  const formatted = fmtByCurrency(entry.amount, currency);
  const type = (entry.entryType || entry.type || "").toLowerCase();
  if (type === "credit") return { text: `+${formatted}`, color: "text-green-600" };
  if (type === "debit") return { text: `-${formatted}`, color: "text-red-500" };
  return { text: formatted, color: "text-gray-900" };
};

const PERIOD_TABS = ["MTD", "Last 30d", "Last 90d", "Custom"];
const PAGE_SIZE = 20;

/** Frontend date filter — compares entry.createdAt against the selected period */
const filterByPeriod = (records, period, customFrom, customTo) => {
  const now = new Date();
  now.setHours(23, 59, 59, 999); // include today fully

  if (period === "MTD") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return records.filter((r) => r.createdAt && new Date(r.createdAt) >= start);
  }
  if (period === "Last 30d") {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return records.filter((r) => r.createdAt && new Date(r.createdAt) >= start);
  }
  if (period === "Last 90d") {
    const start = new Date(now);
    start.setDate(start.getDate() - 90);
    return records.filter((r) => r.createdAt && new Date(r.createdAt) >= start);
  }
  if (period === "Custom") {
    return records.filter((r) => {
      if (!r.createdAt) return false;
      const d = new Date(r.createdAt);
      if (customFrom && d < new Date(customFrom)) return false;
      if (customTo   && d > new Date(customTo + "T23:59:59")) return false;
      return true;
    });
  }
  return records;
};

// ─── Extract USDC balance from vendor payload ─────────────────────────────────
const extractUsdcBalance = (balancePayload) => {
  if (!balancePayload) return null;
  if (Array.isArray(balancePayload)) {
    const entry = balancePayload.find(
      (b) => String(b.symbol).toUpperCase() === "USDC"
    );
    return entry ? { balance: entry.balance, chain: entry.chain } : null;
  }
  if (typeof balancePayload === "object") {
    if (balancePayload.symbol === "USDC") return balancePayload;
    if (balancePayload.USDC) return { balance: balancePayload.USDC };
  }
  return null;
};

// ─── Component ────────────────────────────────────────────────────────────────
const Treasury = () => {
  const userId = useSelector((s) => s.auth.userId);
  const quotePermissions = useSelector((s) => s.auth.quotePermissions);
  const navigate = useNavigate();

  useEffect(() => {
    if (!quotePermissions.canOnramp && !quotePermissions.canOfframp) {
      navigate("/receiver/overview", { replace: true });
    }
  }, [quotePermissions, navigate]);

  // Balance
  const [vaultData, setVaultData] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState(false);

  // All transactions (fetched once)
  const [allTxns, setAllTxns] = useState([]);
  const [txnLoading, setTxnLoading] = useState(true);

  // Filters & pagination
  const [period, setPeriod] = useState("MTD");
  const [fromDate, setFromDate] = useState(""); // kept for Custom — hidden this phase
  const [toDate, setToDate]   = useState(""); // kept for Custom — hidden this phase
  const [page, setPage] = useState(1);

  // ── Load vault balance once ────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    setBalanceLoading(true);
    getVaultBalance(userId)
      .then((res) => setVaultData(res?.data || null))
      .catch(() => setBalanceError(true))
      .finally(() => setBalanceLoading(false));
  }, [userId]);

  // ── Load all ledger entries once (large batch) ─────────────────────────────
  // Period filtering is done on the frontend against entry.createdAt
  useEffect(() => {
    if (!userId) return;
    setTxnLoading(true);
    getVaultLedger(userId, { page: 1, limit: 200 })
      .then((res) => setAllTxns(res.data || []))
      .catch(() => setAllTxns([]))
      .finally(() => setTxnLoading(false));
  }, [userId]);

  // ── Frontend filter by period ──────────────────────────────────────────────
  const filteredTxns = useMemo(
    () => filterByPeriod(allTxns, period, fromDate, toDate),
    [allTxns, period, fromDate, toDate]
  );

  const totalPages   = Math.max(1, Math.ceil(filteredTxns.length / PAGE_SIZE));
  const displayedTxns = filteredTxns.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 whenever the filter changes
  const handlePeriodChange = (t) => {
    setPeriod(t);
    setPage(1);
  };

  const usdcEntry = extractUsdcBalance(vaultData?.balance);

  return (
    <AppShell>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treasury</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your USDC wallet</p>
        </div>
      </div>

      {/* Wallet balance card */}
      <div className="mb-6">
        {balanceLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-64 animate-pulse">
            <div className="h-3 bg-gray-100 rounded w-24 mb-4" />
            <div className="h-8 bg-gray-100 rounded w-40 mb-2" />
          </div>
        ) : balanceError || !usdcEntry ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-64">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              Wallet Balance
            </p>
            <p className="text-3xl font-bold text-gray-900">0.0000000</p>
            <p className="text-xs text-gray-400 mt-1">USDC</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl p-6 inline-block min-w-[16rem]">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1">
              Wallet Balance
            </p>
            <p className="text-3xl font-bold text-gray-900">
              {fmtUsdc(usdcEntry.balance)}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              USDC{usdcEntry.chain ? ` · ${usdcEntry.chain}` : ""}
            </p>
          </div>
        )}
      </div>

      {/* Transactions table */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Transactions</h2>
        </div>

        {/* Period tabs */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100">
          <span className="text-sm text-gray-500">Period:</span>
          <div className="flex items-center gap-2">
            {PERIOD_TABS.filter((t) => t !== "Custom").map((t) => (
              <button
                key={t}
                onClick={() => handlePeriodChange(t)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  period === t
                    ? "bg-brand text-white border-brand"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {/* Custom date range — hidden for this phase, kept for future use */}
          {false && period === "Custom" && (
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                className="border border-gray-200 rounded-lg px-3 h-9 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/30 w-36"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                className="border border-gray-200 rounded-lg px-3 h-9 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/30 w-36"
              />
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {["SN", "Type", "Date", "Status", "Amount", ""].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txnLoading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="border-t border-gray-100 animate-pulse">
                    {[...Array(6)].map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-100 rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : displayedTxns.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      heading="No transactions found"
                      subtext={
                        period === "MTD"
                          ? "No transactions this month."
                          : `No transactions in the ${period} window.`
                      }
                    />
                  </td>
                </tr>
              ) : (
                displayedTxns.map((entry, idx) => {
                  const { text, color } = amountDisplay(entry);
                  const txnId = entry.transactionId || entry.id;
                  const entryType = entry.entryType || entry.type;
                  return (
                    <tr
                      key={txnId || `row-${entry.createdAt}-${entry.amount}`}
                      className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {(page - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 capitalize">
                        {entryType ? entryType.toLowerCase() : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(entry.createdAt)}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium ${statusColor(entry.status || entryType)}`}>
                        {statusLabel(entry.status || entryType)}
                      </td>
                      <td className={`px-4 py-3 text-sm font-semibold ${color}`}>
                        {text} <span className="text-xs font-normal text-gray-400">{entry.currency || "USDC"}</span>
                      </td>
                      <td className="px-4 py-3" />
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </AppShell>
  );
};

export default Treasury;
